import { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import {
  IssuerConfirmPaymentModal,
  IssuerConfirmPaymentModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerConfirmPaymentModal.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import {
  usePaymentArchiveByIdMutation,
  usePaymentSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/payments.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Button,
  DeclineModal,
  DeclineModalProps,
  DisclosureListItem,
  ModalProviderContext,
} from 'ui';
import { goodFormatUSDCurrency } from 'ui/utils';
import {
  PaymentDirection,
  PaymentPaginateResponse,
  PaymentSetRejectedStatusRequest,
  PaymentStatus,
} from 'api';

interface IssuerPaymentItemProps {
  payment: PaymentPaginateResponse;
  navigateToHolderPage?: boolean;
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

export const IssuerPaymentItem = ({
  payment,
  navigateToHolderPage,
  isDeleted = false,
  showArchiveButton = false,
}: IssuerPaymentItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [params] = useSearchParams();
  const { pathname } = useLocation();

  const {
    id,
    memo,
    status,
    amount,
    direction,
    register_obj,
    transaction_id,
    price_per_share,
    requested_at_date,
    confirmation_date,
    dividend_event_id,
    shareholder_entity,
  } = payment;

  const { mutateAsync: rejectPayment } = usePaymentSetRejectedMutation();
  const { mutateAsync: archivePayment } = usePaymentArchiveByIdMutation();

  const handleRejectPayment = useCallback(
    async (payload: PaymentSetRejectedStatusRequest) => {
      await rejectPayment({ ...payload, id });
    },
    [id, rejectPayment],
  );

  const pendingStatusAction = useMemo(
    () =>
      status === PaymentStatus.Pending ? (
        <div className="col-span-full flex items-center justify-end space-x-4">
          <Button
            className="w-[150px]"
            variant="secondary"
            onClick={() =>
              openModal<DeclineModalProps<PaymentSetRejectedStatusRequest>>(DeclineModal, {
                onConfirm: handleRejectPayment,
              })
            }
          >
            Decline
          </Button>
          <Button
            className="w-[150px]"
            onClick={() =>
              openModal<IssuerConfirmPaymentModalProps>(IssuerConfirmPaymentModal, {
                amount,
                payment_id: id,
                price_per_share: price_per_share || 0,
              })
            }
          >
            Confirm
          </Button>
        </div>
      ) : null,
    [amount, handleRejectPayment, id, openModal, price_per_share, status],
  );

  const handleOpenFullDividend = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const handleOpenFullTransaction = useCallback(() => {
    if (!transaction_id) return;
    openModal(IssuerTransactionsOperationInfoModal, {
      id: transaction_id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, registerCountry, registerId, transaction_id]);

  const handleOpenFullPayment = useCallback(() => {
    if (!id) return;
    openModal(IssuerActivitiesOperationInfoModal, {
      paymentId: id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, id, registerCountry, registerId]);

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [
      [
        'Payment ID',
        id ? (
          <span
            className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
            onClick={handleOpenFullPayment}
          >
            #{id}
          </span>
        ) : (
          '-'
        ),
      ],
    ];

    if (shareholder_entity?.entity_name && navigateToHolderPage) {
      info.push([
        'Investor',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder(shareholder_entity?.id?.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {shareholder_entity?.entity_name}
        </Link>,
      ]);
    }
    if (transaction_id) {
      info.push([
        'Transaction ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullTransaction}
        >
          #{transaction_id}
        </span>,
      ]);
    }

    if (price_per_share) {
      info.push(['Price per share/unit', goodFormatUSDCurrency(price_per_share)]);
    }
    if (dividend_event_id) {
      info.push([
        `${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`,
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullDividend(dividend_event_id)}
        >
          #{dividend_event_id}
        </span>,
      ]);
    }

    info.push(['Register', register_obj.name]);

    if (memo) {
      info.push(['Memo', memo]);
    }

    if (!isDeleted && !!pendingStatusAction) {
      info.push([pendingStatusAction, <></>]);
    }

    if (!isDeleted && showArchiveButton) {
      info.push([
        <IssuerOperationArchiveButton id={id} entityName="payment" onArchive={archivePayment} />,
        <></>,
      ]);
    }

    return info;
  }, [
    id,
    handleOpenFullPayment,
    shareholder_entity?.entity_name,
    shareholder_entity?.id,
    navigateToHolderPage,
    transaction_id,
    price_per_share,
    dividend_event_id,
    register_obj.name,
    memo,
    isDeleted,
    pathname,
    params,
    handleOpenFullTransaction,
    getRegisterTypeStrings,
    handleOpenFullDividend,
    pendingStatusAction,
    showArchiveButton,
    archivePayment,
  ]);

  return (
    <DisclosureListItem
      leftMainString={
        navigateToHolderPage ? (
          <span className="text-[14px] leading-[22px] capitalize text-start">
            {direction === PaymentDirection.FundToInvestor ? 'To ' : 'From '}
            <Link
              state={{ from: pathname + '?' + params.toString() }}
              to={{
                pathname: issuerDashboardPaths.dynamic.holder(shareholder_entity?.id?.toString()),
                search: params.toString(),
              }}
              className="text-blue-700 hover:underline"
            >
              {shareholder_entity?.entity_name || '-'}
            </Link>
          </span>
        ) : (
          <span className="text-[14px] leading-[22px] capitalize text-start">
            {direction === PaymentDirection.FundToInvestor ? 'To ' : 'From '}
            <span className="text-blue-700">{shareholder_entity?.entity_name || '-'}</span>
          </span>
        )
      }
      classes={{
        leftContent: 'w-full max-w-[500px]',
        rightMainString: 'text-nowrap text-right',
        rightContentUp: 'justify-items-end',
      }}
      rightMainString={goodFormatUSDCurrency(amount)}
      rightContent={
        <span className="flex gap-4">
          {dividend_event_id && (
            <span
              className={
                'text-[12px] leading-[20px] text-white bg-blue-400 w-fit select-none ' +
                'rounded-[1000px] px-2 py-[2px] capitalize col-start-1 col-end-2'
              }
            >
              {getRegisterTypeStrings('dividend', { capitalize: true })}
            </span>
          )}
          <ActivityStatusBadge status={status || ''} />
        </span>
      }
      leftSubduedContentSet={[
        dateToPrint(confirmation_date || requested_at_date) || '-',
        // fixNameEnum(
        //   direction === PaymentDirection.FundToInvestor
        //     ? TransactionType.Redemption
        //     : TransactionType.Subscription,
        // ),
      ]}
      bottomContent={bottomContentData}
    />
  );
};
